import { Box, ColorProps, Divider, Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { ShelfChangeStatus } from "__graphql__/types";
import { RescueBagIcon, WarningThreeIcon } from "ui/Icons/Icons";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { Status } from "ui/Status/Status";
import { BodyM, HeaderM, TitleS } from "ui/Typography/Typography";

type RescueBagDetailsCardProps = {
  sku: string;
  name: string | null;
  imageUrl: string | null;
  stock: number;
  status: ShelfChangeStatus;
  quantity: number;
  onChangeQuantity: (quantity: number) => void;
};

function StockCounter({
  title,
  value,
  numberColor,
  bgColor,
}: {
  title: string;
  value: number;
  numberColor?: ColorProps["color"];
  bgColor?: string;
}) {
  return (
    <Flex direction="column" alignItems="center">
      <TitleS color="grey.600">{title}</TitleS>
      <HeaderM paddingX="s100" borderRadius="8px" bg={bgColor} color={numberColor} mt="s50">
        {value}
      </HeaderM>
    </Flex>
  );
}

export function RescueBagDetailsCard({
  sku,
  name,
  imageUrl,
  stock,
  status,
  quantity,
  onChangeQuantity,
}: RescueBagDetailsCardProps) {
  const intl = useIntl();
  const isCompleted = status === ShelfChangeStatus.completed;
  const newStock = Math.max(0, stock - quantity);

  return (
    <Flex direction="column" w="100%">
      <Box p="s200" bg="white" w="100%" data-testid="rescue-bag-details-card">
        {isCompleted ? (
          <Flex mb="s200">
            <Status
              label={intl.formatMessage({
                id: "pages.shelfChanges.shelfChangeDetailsCard.status.completed",
              })}
              bgColor="veggie.500"
              textColor="white"
            />
          </Flex>
        ) : (
          <Flex
            align="center"
            gap="s150"
            bg="sunny.200"
            borderRadius="sm"
            p="s150"
            mb="s200"
            data-testid="rescue-bag-warning"
          >
            <WarningThreeIcon color="orange.500" boxSize="24px" flexShrink={0} />
            <BodyM color="grey.800">
              {intl.formatMessage({
                id:
                  stock === 0
                    ? "pages.inventory.shelf-changes.rescue-bag.no-stock-instruction"
                    : "pages.inventory.shelf-changes.rescue-bag.instruction",
              })}
            </BodyM>
          </Flex>
        )}

        <ProductInformation
          bg="grey.100"
          name={name ?? "Unknown Product"}
          imageUrl={imageUrl ?? undefined}
          sku={sku}
          stock={stock}
          showStockPill
          variantSize="sm"
          showLeftSideBar={isCompleted}
          leftSideBarProps={{ bgColor: "veggie.500" }}
        />
      </Box>

      {!isCompleted && stock > 0 && (
        <Flex direction="column" gap="s200" px="s200" pt="s200">
          <Flex
            align="center"
            justify="space-evenly"
            bg="white"
            w="100%"
            borderRadius="8px"
            py="s200"
            data-testid="rescue-bag-stock-counter"
          >
            <StockCounter
              title={intl.formatMessage({ id: "inventory-change-reason.counter.stock" })}
              value={stock}
            />
            <StockCounter
              title={intl.formatMessage({ id: "pages.inbound.inbound-outbound.outbounded-title" })}
              value={-quantity}
              numberColor="purple.500"
            />
            <StockCounter
              title={intl.formatMessage({ id: "pages.inbound.inbound-outbound.new-stock-title" })}
              value={newStock}
              numberColor="pinkFlink.500"
              bgColor="pinkFlink.100"
            />
          </Flex>

          <Box
            p="s200"
            bg="white"
            w="100%"
            borderRadius="8px"
            data-testid="rescue-bag-outbound-panel"
          >
            <TitleS mb="s100">
              {intl.formatMessage({ id: "pages.inventory.shelf-changes.rescue-bag.reason-label" })}
            </TitleS>
            <Flex
              align="center"
              gap="s100"
              border="1px solid"
              borderColor="grey.200"
              borderRadius="sm"
              px="s150"
              py="s100"
              mb="s200"
              data-testid="rescue-bag-reason"
            >
              <RescueBagIcon h="20px" w="20px" color="grey.800" />
              <BodyM color="grey.800">
                {intl.formatMessage({ id: "pages.inventory.shelf-changes.rescue-bag.reason" })}
              </BodyM>
            </Flex>

            <Divider mb="s200" />

            <IncreaseDecrease
              withInput
              label={intl.formatMessage({
                id: "pages.inventory.shelf-changes.rescue-bag.quantity-label",
              })}
              count={quantity}
              changeHandler={(value) => onChangeQuantity(Math.max(0, Math.min(value, stock)))}
              isIncreaseBtnDisabled={quantity >= stock}
              isDecreaseBtnDisabled={quantity <= 0}
              dataTestId="rescue-bag-quantity"
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
